import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { CONSENT_TEXT } from "@/lib/constants";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { Phone, ShieldCheck, MessageSquare, CheckCircle2, XCircle } from "lucide-react";

const TYPE_LABELS = {
  invoice_due: "Invoice due today", invoice_overdue: "Invoice overdue", invoice_paid: "Invoice paid",
  payout_expected: "Payout expected", payout_missed: "Payout missed", payout_received: "Payout received",
  weekly_digest: "Weekly summary",
};

export default function Notifications() {
  const [pref, setPref] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("+1");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, l] = await Promise.all([api.get("/notifications/preferences"), api.get("/notifications/logs")]);
      setPref(p.data); setLogs(l.data);
      if (p.data.phone_number) setPhone(p.data.phone_number);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitPhone = async () => {
    if (!consent) { toast.error("Please agree to the consent statement first."); return; }
    setBusy(true);
    try {
      const e164 = phone.trim().startsWith("+") ? phone.trim().replace(/[^\d+]/g, "") : country + phone.replace(/\D/g, "");
      await api.post("/notifications/phone", { phone_number: e164, consent: true, consent_source: "notifications_page" });
      toast.success("Confirmation text sent (mock). Reply YES to confirm.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try { await api.post("/notifications/verify", { reply: "YES" }); toast.success("Phone confirmed. Alerts enabled."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleSms = async (on) => {
    try { await api.post(on ? "/notifications/enable" : "/notifications/disable"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleType = async (key, val) => {
    try { const { data } = await api.patch("/notifications/types", { [key]: val }); setPref(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removePhone = async () => {
    try { await api.delete("/notifications/phone"); toast.success("Number removed"); setPhone(""); setConsent(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-40 rounded-lg" /><Skeleton className="h-64 rounded-lg" /></div>;

  const subscribed = pref?.consent_status === "subscribed" && pref?.phone_verified;
  const pending = pref?.consent_status === "pending_confirmation";

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Notifications</h1>
        <p className="text-espresso-soft mt-1">Optional transactional text alerts about your account. Never marketing.</p>
      </div>

      {/* Status card */}
      <div className="border rounded-lg bg-white p-6 space-y-4" data-testid="sms-status-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="p-2 rounded-md bg-oatmeal"><Phone size={18} /></span>
            <div>
              <p className="text-sm font-semibold">{pref?.phone_number || "No phone number added"}</p>
              <p className="text-xs text-espresso-soft flex items-center gap-1.5">
                {subscribed ? <><CheckCircle2 size={13} className="text-emerald-600" /> Verified & subscribed</>
                  : pending ? <>Pending confirmation</>
                  : pref?.consent_status === "unsubscribed" ? <><XCircle size={13} className="text-rose-500" /> Unsubscribed</>
                  : "Not set up"}
              </p>
            </div>
          </div>
          {subscribed && (
            <div className="flex items-center gap-3">
              <Label htmlFor="sms-master" className="text-sm">Alerts</Label>
              <Switch id="sms-master" checked={pref?.sms_enabled} onCheckedChange={toggleSms} data-testid="sms-master-toggle" />
              <Button variant="outline" size="sm" onClick={removePhone} data-testid="remove-phone-btn">Remove number</Button>
            </div>
          )}
        </div>

        {!subscribed && (
          <div className="space-y-4 pt-2 border-t">
            <div className="space-y-1.5">
              <Label htmlFor="phone">Mobile number</Label>
              <div className="flex gap-2">
                <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="country-code"
                  className="h-9 rounded-md border border-input bg-white px-2 text-sm">
                  {[["+1","US/CA"],["+44","UK"],["+61","AU"],["+91","IN"],["+49","DE"],["+33","FR"],["+65","SG"]].map(([code,label]) => (
                    <option key={code} value={code}>{label} {code}</option>
                  ))}
                </select>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="4155550142" className="flex-1" data-testid="phone-input" />
              </div>
              <p className="text-xs text-ink-muted">Stored in E.164 format, e.g. {country}4155550142.</p>
            </div>
            <label className="flex gap-3 items-start cursor-pointer">
              <Checkbox checked={consent} onCheckedChange={setConsent} className="mt-0.5" data-testid="consent-checkbox" />
              <span className="text-xs text-espresso-soft leading-relaxed">{CONSENT_TEXT}{" "}
                <a href="/sms-terms" target="_blank" rel="noopener noreferrer" className="underline">SMS Terms</a> · <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> · <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a>
              </span>
            </label>
            {pending ? (
              <div className="flex gap-2">
                <Button onClick={confirm} disabled={busy} data-testid="confirm-phone-btn">Simulate reply "YES"</Button>
                <Button variant="outline" onClick={submitPhone} disabled={busy}>Resend</Button>
              </div>
            ) : (
              <Button onClick={submitPhone} disabled={busy || !consent} data-testid="enable-sms-btn">
                {busy ? "Sending…" : "Send confirmation text"}
              </Button>
            )}
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 inline-block">
              DEV / MOCK MODE — no real SMS is sent. Messages are logged & shown below.
            </p>
          </div>
        )}
      </div>

      {/* Types */}
      <div className="border rounded-lg bg-white p-6" data-testid="notification-types">
        <div className="flex items-center gap-2 mb-4"><ShieldCheck size={18} className="text-warmmuted" /><h2 className="font-heading font-bold">Which alerts to receive</h2></div>
        <div className="grid sm:grid-cols-2 gap-3">
          {Object.entries(TYPE_LABELS).map(([key, label]) => (
            <label key={key} className="flex items-center justify-between border rounded-md px-3 py-2.5">
              <span className="text-sm">{label}</span>
              <Switch checked={pref?.types?.[key] ?? true} onCheckedChange={(v) => toggleType(key, v)} disabled={!subscribed} data-testid={`type-${key}`} />
            </label>
          ))}
        </div>
        <p className="text-xs text-warmmuted mt-3">A global STOP always overrides these individual preferences.</p>
      </div>

      {/* History */}
      <div className="border rounded-lg bg-white p-6" data-testid="notification-history">
        <div className="flex items-center gap-2 mb-4"><MessageSquare size={18} className="text-warmmuted" /><h2 className="font-heading font-bold">Message history</h2></div>
        {logs.length === 0 ? (
          <p className="text-sm text-espresso-soft">No messages yet. Alerts you receive will appear here.</p>
        ) : (
          <div className="space-y-2">
            {logs.map((l) => (
              <div key={l.id} className="border rounded-md p-3" data-testid={`log-${l.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-espresso-soft">{l.message_type.replace(/_/g, " ")}</span>
                  <span className="text-xs text-warmmuted">{formatDate(l.sent_at)}</span>
                </div>
                <p className="text-sm mt-1 text-espresso">{l.body_text}</p>
                <p className="text-[11px] text-warmmuted mt-1">to {l.recipient_phone} · {l.provider} · {l.delivery_status}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
